<template>
    <div
        class="overflow-hidden rounded-xl border bg-white"
        :class="borderClass"
    >
        <!-- Header: what was run, with what, and an unmistakable note that no
             model was involved in producing the numbers below. -->
        <div
            class="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-gray-100 bg-gray-50 px-4 py-2"
        >
            <CommandLineIcon class="h-4 w-4 shrink-0 text-gray-400" />
            <span class="font-mono text-xs font-medium text-gray-700">{{
                result.command
            }}</span>
            <span
                v-for="parameter in parameters"
                :key="parameter.name"
                class="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-500 ring-1 ring-gray-200"
            >
                {{ parameter.name }}={{ parameter.value }}
            </span>
            <span
                class="ml-auto shrink-0 text-[10px] uppercase tracking-wide"
                :class="badgeClass"
            >
                {{ badgeLabel }}
            </span>
        </div>

        <div v-if="isFailed" class="px-4 py-3 text-sm text-gray-600">
            {{ failureMessage }}
        </div>

        <!-- "/tool help" answers from the command catalogue, so it is laid out
             as the reference it is rather than as arbitrary tool output. -->
        <div
            v-else-if="helpCommands.length > 0"
            class="divide-y divide-gray-100"
        >
            <div
                v-for="entry in helpCommands"
                :key="entry.command"
                class="px-4 py-2.5"
            >
                <p class="font-mono text-xs text-gray-800">{{ entry.usage }}</p>
                <p class="mt-0.5 text-xs text-gray-500">
                    {{ t(`vue.${entry.translation_key}`) }}
                </p>
            </div>
        </div>

        <div
            v-else-if="sections.length === 0"
            class="px-4 py-3 text-sm text-gray-500"
        >
            {{ t("vue.ai_slash_result_empty") }}
        </div>

        <div v-else class="divide-y divide-gray-100">
            <div
                v-for="section in sections"
                :key="section.key"
                class="px-4 py-3"
            >
                <p
                    v-if="section.kind !== 'fields' || section.title !== null"
                    class="mb-2 text-[10px] font-medium uppercase tracking-wide text-gray-400"
                >
                    {{ section.title }}
                </p>

                <dl
                    v-if="section.kind === 'fields'"
                    class="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2"
                >
                    <div
                        v-for="field in section.fields"
                        :key="field.key"
                        class="flex items-baseline justify-between gap-3"
                    >
                        <dt class="shrink-0 text-xs text-gray-500">
                            {{ field.label }}
                        </dt>
                        <dd class="min-w-0 text-right text-sm">
                            <AiChatToolResultValue :value="field.value" />
                        </dd>
                    </div>
                </dl>

                <div
                    v-else-if="section.kind === 'table'"
                    class="-mx-1 overflow-x-auto"
                >
                    <table class="w-full min-w-max text-sm">
                        <thead>
                            <tr>
                                <th
                                    v-for="column in section.columns"
                                    :key="column"
                                    class="whitespace-nowrap px-1 pb-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-gray-400"
                                >
                                    {{ humanizeToolResultKey(column) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(row, index) in section.rows"
                                :key="index"
                                class="border-t border-gray-100"
                            >
                                <td
                                    v-for="column in section.columns"
                                    :key="column"
                                    class="whitespace-nowrap px-1 py-1.5"
                                >
                                    <AiChatToolResultValue
                                        :value="row[column]"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    v-else-if="section.kind === 'list'"
                    class="flex flex-wrap gap-1"
                >
                    <span
                        v-for="(item, index) in section.values"
                        :key="`${section.key}-${index}`"
                        class="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600"
                        >{{ item }}</span
                    >
                </div>

                <pre
                    v-else
                    class="overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[11px] text-gray-600"
                    >{{ section.json }}</pre
                >
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { CommandLineIcon } from "@heroicons/vue/24/outline";
import AiChatToolResultValue from "@/Pages/AiChat/components/AiChatToolResultValue.vue";
import type { IAiChatToolResult } from "@/types/AiChat/IAiChat";
import type { IAiSlashCommand } from "@/types/AiChat/IAiSlashCommand";
import {
    buildToolResultSections,
    humanizeToolResultKey,
} from "@/Pages/AiChat/toolResultSections";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{ result: IAiChatToolResult }>();

const isFailed = computed(() => props.result.status !== "success");

const failureMessage = computed(() => {
    const error = props.result.output.error;

    return typeof error === "string" && error !== ""
        ? error
        : t("vue.ai_slash_result_failed");
});

const borderClass = computed(() =>
    isFailed.value ? "border-amber-200" : "border-gray-200",
);

const badgeLabel = computed(() =>
    isFailed.value
        ? t("vue.ai_slash_badge_failed")
        : t("vue.ai_slash_badge_no_ai"),
);

const badgeClass = computed(() =>
    isFailed.value ? "text-amber-600" : "text-gray-400",
);

const parameters = computed(() =>
    Object.entries(props.result.parameters).map(([name, value]) => ({
        name,
        value: String(value),
    })),
);

// "/tool help" carries the catalogue itself; every other command carries data.
const helpCommands = computed<IAiSlashCommand[]>(() => {
    const commands = props.result.output.commands;

    return Array.isArray(commands) ? (commands as IAiSlashCommand[]) : [];
});

const sections = computed(() => buildToolResultSections(props.result.output));
</script>
